import 'leaflet/dist/leaflet.css';
import { BASEMAP_TILE_URL, BASEMAP_ATTRIBUTION } from '../../basemap.js';
import './map.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import {
  AttributionControl,
  CircleMarker,
  MapContainer,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import Supercluster from 'supercluster';
import type { PointFeature, ClusterFeature, AnyProps } from 'supercluster';
import { PanelFrame } from '../panel-frame.js';
import { MapFilterModal } from '../map-filter-modal.js';
import {
  buildFacilitiesQuery,
  countActiveMapFilters,
  defaultMapFilters,
  type MapFilters,
} from '../map-filters.js';
import { useFacilities, useFacilitiesFacets } from '../../api/facilities-hooks.js';
import type { FacilityPoint } from '../../api/client.js';
import { MapPopupActions } from '../map-popup-actions.js';
import {
  useAppStore,
  usePanelSelection,
  useSetPanelEntity,
  useSetPanelSelection,
  type PanelId,
  type Selection,
  type TabId,
} from '../store.js';
import { panelAccepts } from '../panel-registry.js';
import { useHome, useHomePoints } from '../../api/homes-hooks.js';
import type { HomePoint, HomesQuery } from '../../api/client.js';
import { homeSelection, useOpenHomeRecord } from '../use-open-home-record.js';
import { useHomesFilterLink } from '../homes-filter-link.js';
import { homeValueLabel } from '../homes-filters.js';
import type { PanelParams } from '../components.js';
import { usePanelVisible } from '../panel-visibility.js';
import { useMandateCountries } from '../use-mandate-defaults.js';
import { EUROPE_VIEW, viewForMarkets, type MapView } from '../market-geo.js';

// Facilities Map (interface-brief Phase 2 "Map view"). Plots one point per
// geocoded CQC location, clustered with supercluster for performance over
// the ~120k UK universe. Channel-linked: clicking a site broadcasts its
// entity to the panel's link group (Company Overview + siblings follow);
// when the channel entity changes elsewhere, the map flies to that site.
//
// No emojis, no legend (per spec) — clusters are accent circles sized by
// count, leaves are small accent dots; rating/sub-segment live in the
// click popup.

interface SitePointProps {
  pointId: string;
  entityId: string | null;
  name: string | null;
  rating: string | null;
  serviceTypes: string[];
  /** 'office' = registered-office coordinates (entity-row lat/lon markets) —
   *  labelled in the popup so office-grade precision isn't read as a site. */
  source: 'site' | 'office';
}

// Opens on the mandated market (mandate → country filter + frame) or the
// Europe frame — never a hardcoded UK view; see use-mandate-defaults.

// Read a CSS custom property off :root so canvas-drawn markers can use the
// platform accent (canvas can't resolve `var(--accent)` itself).
function readToken(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** One-shot camera move once the panel's opening scope resolves (mandate
 *  loaded). Never re-fires — after the first frame the camera belongs to
 *  the user. */
function AdoptView({ view }: { view: MapView | null }) {
  const map = useMap();
  const applied = useRef(false);
  useEffect(() => {
    if (!view || applied.current) return;
    applied.current = true;
    map.setView(view.center, view.zoom);
  }, [view, map]);
  return null;
}

/** Keep Leaflet's internal size in sync with the dockview panel — without
 *  this a resize/redock leaves the map rendered at its old dimensions. */
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(container);
    // One deferred pass after mount — the panel often mounts at 0×0.
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [map]);
  return null;
}

function ClusterLayer({
  points,
  accent,
  focusEntityId,
  onSelect,
}: {
  points: FacilityPoint[];
  accent: string;
  focusEntityId: string | null;
  onSelect: (entityId: string | null) => void;
}) {
  const map = useMap();

  const index = useMemo(() => {
    const sc = new Supercluster<SitePointProps>({ radius: 60, maxZoom: 16, minPoints: 4 });
    const features: PointFeature<SitePointProps>[] = [];
    for (const p of points) {
      if (p.lat == null || p.lng == null) continue;
      features.push({
        type: 'Feature',
        properties: {
          pointId: p.id,
          entityId: p.entityId,
          name: p.name,
          rating: p.rating,
          serviceTypes: p.serviceTypes,
          source: p.source,
        },
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      });
    }
    sc.load(features);
    return sc;
  }, [points]);

  const [rendered, setRendered] = useState<
    Array<ClusterFeature<AnyProps> | PointFeature<SitePointProps>>
  >([]);

  const recompute = useCallback(() => {
    const b = map.getBounds();
    const bbox: [number, number, number, number] = [
      b.getWest(),
      b.getSouth(),
      b.getEast(),
      b.getNorth(),
    ];
    setRendered(index.getClusters(bbox, Math.round(map.getZoom())));
  }, [index, map]);

  useMapEvents({ moveend: recompute, zoomend: recompute });
  useEffect(recompute, [recompute]);

  // Channel → map: when the linked entity changes *from elsewhere* (a
  // Screener/Watchlist row, Company Overview), frame that entity's sites.
  // A company often has many CQC locations, so we fit the bounds of ALL of
  // them rather than diving into one. A selection that originated from
  // clicking a map marker is suppressed — the user is already looking at it,
  // so re-centering/zooming would be jarring.
  const lastFocus = useRef<string | null | undefined>(undefined);
  const selfClickRef = useRef<string | null>(null);
  useEffect(() => {
    // First run: adopt whatever is already selected as the baseline without
    // moving, so opening the panel shows the overview rather than diving
    // into the persisted channel entity. Only genuinely new selections frame.
    if (lastFocus.current === undefined) {
      lastFocus.current = focusEntityId;
      return;
    }
    if (!focusEntityId || focusEntityId === lastFocus.current) return;

    // Selection came from clicking a marker on this map — don't move.
    if (selfClickRef.current === focusEntityId) {
      selfClickRef.current = null;
      lastFocus.current = focusEntityId;
      return;
    }

    const sites = points.filter(
      (p) => p.entityId === focusEntityId && p.lat != null && p.lng != null,
    );
    // Data may not have loaded yet — leave lastFocus unset so we retry once
    // the points arrive.
    if (sites.length === 0) return;
    lastFocus.current = focusEntityId;

    if (sites.length === 1) {
      const s = sites[0]!;
      // Pan to it; only zoom in if we're currently zoomed well out.
      map.setView([s.lat!, s.lng!], Math.max(map.getZoom(), 11), { animate: true });
      return;
    }
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (const s of sites) {
      minLat = Math.min(minLat, s.lat!);
      maxLat = Math.max(maxLat, s.lat!);
      minLng = Math.min(minLng, s.lng!);
      maxLng = Math.max(maxLng, s.lng!);
    }
    map.fitBounds(
      [
        [minLat, minLng],
        [maxLat, maxLng],
      ],
      { padding: [48, 48], maxZoom: 13, animate: true },
    );
  }, [focusEntityId, points, map]);

  return (
    <>
      {rendered.map((f) => {
        const [lng, lat] = f.geometry.coordinates as [number, number];
        const props = f.properties as SitePointProps & {
          cluster?: boolean;
          cluster_id?: number;
          point_count?: number;
          point_count_abbreviated?: number | string;
        };
        if (props.cluster) {
          const count = props.point_count ?? 0;
          const radius = 9 + Math.min(20, Math.log2(count + 1) * 3.5);
          return (
            <CircleMarker
              key={`c-${props.cluster_id}`}
              center={[lat, lng]}
              radius={radius}
              pathOptions={{
                color: accent,
                weight: 1,
                opacity: 0.9,
                fillColor: accent,
                fillOpacity: 0.32,
              }}
              eventHandlers={{
                click: () => {
                  const z = index.getClusterExpansionZoom(props.cluster_id!);
                  map.flyTo([lat, lng], z);
                },
              }}
            >
              <Tooltip permanent direction="center" className="hc-cluster-label" offset={[0, 0]}>
                {props.point_count_abbreviated ?? count}
              </Tooltip>
            </CircleMarker>
          );
        }
        const entityId = props.entityId;
        return (
          <CircleMarker
            key={`p-${props.pointId}`}
            center={[lat, lng]}
            radius={4}
            pathOptions={{
              color: accent,
              weight: 1,
              fillColor: accent,
              fillOpacity: 0.85,
            }}
            eventHandlers={{
              click: () => {
                // Mark this as a self-originated selection so the focus
                // effect doesn't recenter/zoom onto the marker we clicked.
                selfClickRef.current = entityId;
                onSelect(entityId);
              },
            }}
          >
            <Popup>
              <div className="hc-map-popup-title">{props.name ?? 'Unnamed site'}</div>
              <div className="hc-map-popup-meta">
                {props.serviceTypes.slice(0, 3).join(' · ') ||
                  (props.source === 'office' ? 'Registered office' : 'CQC location')}
                {props.rating ? ` — ${props.rating.replace(/_/g, ' ')}` : ''}
              </div>
              {entityId ? (
                <MapPopupActions entityId={entityId} />
              ) : (
                <div className="hc-map-popup-meta">No linked company</div>
              )}
            </Popup>
          </CircleMarker>
        );
      })}
    </>
  );
}

/** Channel → map for a HOME selection (Homes screener, Home record). The universe layer plots company sites
 *  (facility_points), so a home cannot be found among those dots — an unlinked home has none, and a linked one's
 *  operator is often outside the loaded sample. The home's own coordinate is fetched instead: the map flies to it
 *  and rings it. Opening the panel with a home already selected rings it without moving (same rule as the entity
 *  focus: only genuinely new selections move the camera). */
function HomeFocusLayer({
  homeKey,
  accent,
  onStatus,
  selfClickRef,
}: {
  homeKey: string | null;
  accent: string;
  onStatus: (s: string | null) => void;
  /** Set by a click on a home pin of this map: the user is already looking at it, so don't move. */
  selfClickRef: { current: string | null };
}) {
  const map = useMap();
  const { data, isError } = useHome(homeKey);
  const openHomeRecord = useOpenHomeRecord();
  const loc = data?.home.location;
  const lat = typeof loc?.['lat'] === 'number' ? (loc['lat'] as number) : null;
  const lon = typeof loc?.['lon'] === 'number' ? (loc['lon'] as number) : null;
  const pinLevel = typeof loc?.['pin_level'] === 'string' ? (loc['pin_level'] as string) : null;
  const name = data?.home.identity.name ?? null;
  const resolvedKey = data?.home.identity.home_key ?? null;
  const operatorEntityId = (data?.home.operator['operator_entity_id'] as string | null) ?? null;

  const lastMoved = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastMoved.current === undefined) {
      // first run: the selection the panel opened with is the baseline — ring it, don't move
      lastMoved.current = homeKey;
      return;
    }
    if (!homeKey || homeKey === lastMoved.current) return;
    if (selfClickRef.current === homeKey) {
      selfClickRef.current = null;
      lastMoved.current = homeKey;
      return;
    }
    if (!data) return; // not loaded yet — retry when it arrives
    lastMoved.current = homeKey;
    if (lat !== null && lon !== null) {
      map.setView([lat, lon], Math.max(map.getZoom(), pinLevel === 'precise' ? 15 : 12), {
        animate: true,
      });
    }
  }, [homeKey, data, lat, lon, pinLevel, map, selfClickRef]);

  useEffect(() => {
    if (!homeKey) onStatus(null);
    else if (isError) onStatus('Selected home could not be loaded');
    else if (data && (lat === null || lon === null))
      onStatus(`${name ?? 'Selected home'} has no location yet`);
    else onStatus(null);
  }, [homeKey, isError, data, lat, lon, name, onStatus]);

  if (!homeKey || lat === null || lon === null) return null;
  return (
    <CircleMarker
      key={`home-${homeKey}`}
      center={[lat, lon]}
      radius={10}
      pathOptions={{ color: accent, weight: 3, fillColor: '#fff', fillOpacity: 0.9 }}
    >
      <Tooltip direction="top" offset={[0, -10]}>
        {name ?? 'Selected home'}
        {pinLevel === 'locality' ? ' · locality pin' : ''}
      </Tooltip>
      <Popup>
        <div className="hc-map-popup-title">{name ?? 'Selected home'}</div>
        <div className="hc-map-popup-meta">
          {pinLevel === 'locality' ? 'Locality pin (not the building)' : 'Precise pin'}
        </div>
        {resolvedKey && (
          <div className="hc-map-popup-actions">
            <button
              type="button"
              className="hc-map-popup-action"
              onClick={() => openHomeRecord({ homeKey: resolvedKey, name, operatorEntityId })}
            >
              Open home record
            </button>
          </div>
        )}
      </Popup>
    </CircleMarker>
  );
}

interface HomePinProps {
  homeKey: string;
  name: string | null;
  beds: number | null;
  setting: string | null;
  careLevel: string | null;
  operatorEntityId: string | null;
  pinLevel: string | null;
}

/** HOME MODE universe layer: one pin per care home (care_home_index via /homes/points), clustered like the site
 *  layer. Clicking a pin selects that HOME on the channel (Homes highlights it, Home record opens it, the operator
 *  Record follows its operator). */
function HomeClusterLayer({
  points,
  accent,
  onSelect,
  frameKey,
}: {
  points: HomePoint[];
  accent: string;
  onSelect: (p: HomePinProps) => void;
  /** Changes when the linked screener's filters change: the map then frames the homes that match. */
  frameKey: string;
}) {
  const map = useMap();
  // Frame the result once per filter change (not on every refetch, and not on the panel's first load — the opening
  // camera belongs to AdoptView).
  const framedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (framedFor.current === undefined) {
      framedFor.current = frameKey;
      return;
    }
    if (framedFor.current === frameKey || points.length === 0) return;
    framedFor.current = frameKey;
    let minLat = Infinity,
      maxLat = -Infinity,
      minLng = Infinity,
      maxLng = -Infinity;
    for (const p of points) {
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lon);
      maxLng = Math.max(maxLng, p.lon);
    }
    map.fitBounds(
      [
        [minLat, minLng],
        [maxLat, maxLng],
      ],
      { padding: [32, 32], maxZoom: 13, animate: true },
    );
  }, [frameKey, points, map]);
  const openHomeRecord = useOpenHomeRecord();
  const index = useMemo(() => {
    const sc = new Supercluster<HomePinProps>({ radius: 60, maxZoom: 16, minPoints: 4 });
    sc.load(
      points.map((p) => ({
        type: 'Feature' as const,
        properties: {
          homeKey: p.home_key,
          name: p.name,
          beds: p.beds,
          setting: p.setting,
          careLevel: p.care_level,
          operatorEntityId: p.operator_entity_id,
          pinLevel: p.pin_level,
        },
        geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] },
      })),
    );
    return sc;
  }, [points]);
  const [rendered, setRendered] = useState<
    Array<ClusterFeature<AnyProps> | PointFeature<HomePinProps>>
  >([]);
  const recompute = useCallback(() => {
    const b = map.getBounds();
    setRendered(
      index.getClusters(
        [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        Math.round(map.getZoom()),
      ),
    );
  }, [index, map]);
  useMapEvents({ moveend: recompute, zoomend: recompute });
  useEffect(recompute, [recompute]);

  return (
    <>
      {rendered.map((f) => {
        const [lng, lat] = f.geometry.coordinates as [number, number];
        const props = f.properties as HomePinProps & {
          cluster?: boolean;
          cluster_id?: number;
          point_count?: number;
          point_count_abbreviated?: number | string;
        };
        if (props.cluster) {
          const count = props.point_count ?? 0;
          const radius = 9 + Math.min(20, Math.log2(count + 1) * 3.5);
          return (
            <CircleMarker
              key={`hc-${props.cluster_id}`}
              center={[lat, lng]}
              radius={radius}
              pathOptions={{
                color: accent,
                weight: 1,
                opacity: 0.9,
                fillColor: accent,
                fillOpacity: 0.32,
              }}
              eventHandlers={{
                click: () =>
                  map.flyTo([lat, lng], index.getClusterExpansionZoom(props.cluster_id!)),
              }}
            >
              <Tooltip permanent direction="center" className="hc-cluster-label" offset={[0, 0]}>
                {props.point_count_abbreviated ?? count}
              </Tooltip>
            </CircleMarker>
          );
        }
        const meta = [
          props.beds !== null ? `${Math.round(props.beds)} beds` : null,
          props.setting ? homeValueLabel('setting', props.setting) : null,
          props.careLevel ? homeValueLabel('careLevel', props.careLevel) : null,
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <CircleMarker
            key={`hp-${props.homeKey}`}
            center={[lat, lng]}
            radius={4}
            pathOptions={{ color: accent, weight: 1, fillColor: accent, fillOpacity: 0.85 }}
            eventHandlers={{ click: () => onSelect(props) }}
          >
            <Popup>
              <div className="hc-map-popup-title">{props.name ?? 'Unnamed home'}</div>
              <div className="hc-map-popup-meta">
                {meta || 'Care home'}
                {props.pinLevel === 'locality' ? ' — locality pin' : ''}
              </div>
              <div className="hc-map-popup-actions">
                <button
                  type="button"
                  className="hc-map-popup-action"
                  onClick={() =>
                    openHomeRecord({
                      homeKey: props.homeKey,
                      name: props.name,
                      operatorEntityId: props.operatorEntityId,
                    })
                  }
                >
                  Open home record
                </button>
              </div>
            </Popup>
          </CircleMarker>
        );
      })}
    </>
  );
}

/** The Map is in HOME MODE while a home panel (Homes / Home record) shares its channel: it then plots homes only,
 *  never company sites (Alex 2026-10-16). Returns the channel key, or null in the normal site mode. */
const HOME_PANELS = new Set(['homes', 'home-record']);
function useHomeModeChannel(tabId: TabId, panelId: PanelId): string | null {
  return useAppStore((s) => {
    const tab = s.tabs[tabId];
    const me = tab?.panels[panelId];
    if (!tab || !me || me.link.kind !== 'linked') return null;
    const g = me.link.groupId;
    const linked = Object.values(tab.panels).some(
      (p) =>
        p.id !== panelId &&
        p.link.kind === 'linked' &&
        p.link.groupId === g &&
        HOME_PANELS.has(p.component),
    );
    return linked ? `${tabId}:${g}` : null;
  });
}

interface ScopedPoint {
  lat: number;
  lng: number;
  name: string | null;
  entityId: string | null;
}

// An EXPLICIT, seeded pin set — e.g. the Prescription "Show all on map" build
// shortlist, passed via initialState. When present, the Map shows ONLY these
// pins (framed to their bounds) and suppresses the universe layer. This is the
// one way to scope the Map: passive channel broadcasts (Roll-Up / Catchment)
// still never reach it — only a deliberate seed does.
function ScopedPinsLayer({ points, accent }: { points: ScopedPoint[]; accent: string }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    map.fitBounds(
      [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)],
      ],
      { padding: [48, 48], maxZoom: 12 },
    );
  }, [map, points]);
  return (
    <>
      {points.map((p, i) => (
        <CircleMarker
          key={p.entityId ?? i}
          center={[p.lat, p.lng]}
          radius={7}
          pathOptions={{ color: '#fff', weight: 1.5, fillColor: accent, fillOpacity: 0.95 }}
        >
          <Popup>
            <div className="hc-map-popup-title">{p.name ?? 'Target'}</div>
            {p.entityId && <MapPopupActions entityId={p.entityId} />}
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

const MAP_ACCEPTS = panelAccepts('map');

export function MapPanel({ tabId, panelId, initialState }: PanelParams) {
  const [filters, setFilters] = useState<MapFilters>(defaultMapFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const accent = useMemo(() => readToken('--accent', '#a8572a'), []);

  // Opening scope: mandated markets → seed the country filter and frame the
  // camera on them; no mandate → Europe frame, no filter. Applied exactly
  // once, and never when the Map is scoped to an explicit build seed.
  const { countries: mandateCountries, loaded: mandatesLoaded } = useMandateCountries();
  const [adoptedView, setAdoptedView] = useState<MapView | null>(null);
  const seededRef = useRef(false);

  // Explicit "show these targets" seed (Prescription "Show all on map"). Scopes
  // THIS Map instance to the build's located pins; the universe layer is
  // suppressed. A Map opened without a seed is the normal universe reference.
  const scopedPoints = useMemo<ScopedPoint[]>(() => {
    const raw = initialState?.['portfolioPoints'];
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(
        (p): p is ScopedPoint => !!p && typeof p.lat === 'number' && typeof p.lng === 'number',
      )
      .map((p) => ({ lat: p.lat, lng: p.lng, name: p.name ?? null, entityId: p.entityId ?? null }));
  }, [initialState]);
  const scoped = scopedPoints.length > 0;

  // Gates the first facilities fetch: until the mandate is known the filters are not final, and fetching
  // then meant an unfiltered 20,000-point request followed by a second, country-filtered one — the panel
  // sat on "Loading…" twice. Set in the same effect that seeds the filters, so they land in one render.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seededRef.current || !mandatesLoaded || scoped) return;
    seededRef.current = true;
    if (mandateCountries.length > 0) {
      setFilters((f) => ({ ...f, country: new Set<string>(mandateCountries) }));
      setAdoptedView(viewForMarkets(mandateCountries));
    }
    // No mandate → stay on the Europe frame the map opened with.
    setSeeded(true);
  }, [mandatesLoaded, mandateCountries, scoped]);

  // The facilities query is heavy (scans every geocoded CQC location), so
  // only fetch while the Map tab is actually on screen — not on every page
  // load when it's a background tab. React Query caches the result, so
  // switching away and back doesn't refetch within the stale window. Skipped
  // entirely when scoped to a seeded build (no universe layer then).
  const visible = usePanelVisible();
  const query = useMemo(() => buildFacilitiesQuery(filters), [filters]);
  // HOME MODE: a home panel shares this channel → plot homes only, filtered like the linked Homes screener (or, with
  // only a Home record linked, the mandated markets), and never fetch the company-site layer.
  const homeChannel = useHomeModeChannel(tabId, panelId);
  const homeMode = homeChannel !== null && !scoped;
  const channelHomeFilters = useHomesFilterLink((s) =>
    homeChannel ? (s.byKey[homeChannel] ?? null) : null,
  );
  const homePointsQuery = useMemo<HomesQuery>(
    () =>
      channelHomeFilters ??
      (mandateCountries.length > 0 ? { country: [...mandateCountries].sort().join(',') } : {}),
    [channelHomeFilters, mandateCountries],
  );
  const homePoints = useHomePoints(homePointsQuery, visible && homeMode && seeded);

  const { data, isLoading, isError } = useFacilities(query, {
    enabled: visible && !scoped && seeded && !homeMode,
  });
  const { data: facets } = useFacilitiesFacets({ enabled: visible && !scoped && !homeMode });

  const points = scoped ? [] : (data?.points ?? []);
  const total = data?.total ?? 0;
  // Server caps the count for speed; when capped the real total is ≥ `total`.
  const totalLabel = `${total.toLocaleString()}${data?.totalCapped ? '+' : ''}`;
  const activeFilterCount = countActiveMapFilters(filters);

  // Typed channel selection: a HOME is shown by its own coordinate (HomeFocusLayer); an OPERATOR frames its sites
  // in the universe layer. Group / landlord selections do not move the map yet.
  const selection: Selection | null = usePanelSelection(tabId, panelId, MAP_ACCEPTS);
  const focusEntityId = selection?.kind === 'operator' ? selection.key : null;
  const focusHomeKey = selection?.kind === 'home' ? selection.key : null;
  const [homeStatus, setHomeStatus] = useState<string | null>(null);
  const setPanelEntity = useSetPanelEntity(tabId, panelId);
  const setPanelSelection = useSetPanelSelection(tabId, panelId);
  const homeSelfClick = useRef<string | null>(null);
  const selectHomePin = useCallback(
    (p: HomePinProps) => {
      homeSelfClick.current = p.homeKey;
      setPanelSelection(
        homeSelection({ homeKey: p.homeKey, name: p.name, operatorEntityId: p.operatorEntityId }),
      );
    },
    [setPanelSelection],
  );
  const homeCount = homePoints.data?.points.length ?? 0;

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface)',
          }}
        >
          {homeMode ? (
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-secondary)' }}>
              {channelHomeFilters ? 'Homes · following the Homes screener’s filters' : 'Homes'}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              aria-haspopup="dialog"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--s-1)',
                padding: '2px var(--s-2)',
                fontSize: 'var(--t-body)',
                border: '1px solid var(--hairline)',
                borderRadius: 2,
                background: activeFilterCount > 0 ? 'var(--color-accent-100)' : 'var(--surface)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <Filter size={12} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span
                  data-numeric
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--t-meta)',
                    color: 'var(--accent)',
                  }}
                >
                  · {activeFilterCount}
                </span>
              )}
            </button>
          )}
          <div style={{ flex: 1 }} />
          {homeStatus && (
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-secondary)' }}>
              {homeStatus}
            </span>
          )}
          <span
            data-numeric
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--t-meta)',
              color: 'var(--text-tertiary)',
            }}
          >
            {scoped
              ? `${scopedPoints.length} target${scopedPoints.length === 1 ? '' : 's'} from build`
              : homeMode
                ? homePoints.isLoading || !seeded
                  ? 'Loading…'
                  : homePoints.isError
                    ? 'Failed to load'
                    : homePoints.data?.truncated
                      ? `showing ${homeCount.toLocaleString()} homes · filter to narrow`
                      : `${homeCount.toLocaleString()} homes`
                : isLoading || !seeded
                  ? 'Loading…'
                  : isError
                    ? 'Failed to load'
                    : points.length < total
                      ? `showing ${points.length.toLocaleString()} of ${totalLabel} sites · filter to narrow`
                      : `${totalLabel} sites`}
          </span>
        </div>

        <div className="hc-map-root">
          <MapContainer
            center={EUROPE_VIEW.center}
            zoom={EUROPE_VIEW.zoom}
            minZoom={4}
            maxZoom={18}
            preferCanvas
            scrollWheelZoom
            attributionControl={false}
          >
            {/* Required CARTO + OSM credit, rendered very small (see map.css).
                prefix={false} drops Leaflet's default "Leaflet 🇺🇦" branding. */}
            <AttributionControl prefix={false} position="bottomright" />
            <TileLayer
              className="hc-map-tiles"
              url={BASEMAP_TILE_URL}
              attribution={BASEMAP_ATTRIBUTION}
              subdomains="abcd"
              maxZoom={19}
            />
            <InvalidateOnResize />
            <AdoptView view={adoptedView} />
            {homeMode ? (
              <HomeClusterLayer
                points={homePoints.isPlaceholderData ? [] : (homePoints.data?.points ?? [])}
                accent={accent}
                onSelect={selectHomePin}
                frameKey={JSON.stringify(homePointsQuery)}
              />
            ) : (
              <ClusterLayer
                points={points}
                accent={accent}
                focusEntityId={focusEntityId}
                onSelect={setPanelEntity}
              />
            )}
            {scoped && <ScopedPinsLayer points={scopedPoints} accent={accent} />}
            <HomeFocusLayer
              homeKey={focusHomeKey}
              accent={accent}
              onStatus={setHomeStatus}
              selfClickRef={homeSelfClick}
            />
          </MapContainer>
        </div>
      </div>

      <MapFilterModal
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filters}
        facets={facets}
        onChange={setFilters}
      />
    </PanelFrame>
  );
}
